import { useCallback } from 'react';
import { create } from 'zustand';
import { api, type User } from '../api';

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  displayName: string;
  bio?: string;
  photoUrl?: string;
  photoThumbUrl?: string;
  countLinks: number;
  createdAt: string;
}

interface AuthState {
  user: AuthUser | null;
  // True while the page is bootstrapping the session (calling /users/me).
  // The protected layout checks `!user && !isLoading` to decide whether to
  // redirect to /login — without the loading guard a hard refresh fires
  // that redirect before the cookie-backed bootstrap completes, the
  // proxy then bounces /login → /feed (auth-flow path with active cookie),
  // and the original URL gets lost.
  isLoading: boolean;
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;
  clear: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  isLoading: true,

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ isLoading: loading }),

  logout: async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    set({ user: null });
  },

  clear: () => set({ user: null }),
}));

/**
 * Pure cache writer for the authenticated user. Single entry point for
 * every payload that updates the current user — used by HTTP responses
 * (cut / link-request accept) and by the `reloadYourUser` socket event
 * fanned out to other tabs of the same account. Mirrors the
 * `processMy<X>` pattern of session / link / avatar caches: the page
 * just reads from the store, this function decides what to write.
 *
 * Projects the BE `User` shape onto the slimmer `AuthUser` we keep in
 * memory — same subset that QueryProvider seeds at boot — so the store
 * never carries fields the rest of the app doesn't read.
 */
export function processMyUser(setUser: (u: AuthUser | null) => void, user: User) {
  setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    displayName: user.displayName,
    bio: user.bio,
    photoUrl: user.photoUrl,
    photoThumbUrl: user.photoThumbUrl,
    countLinks: user.countLinks,
    createdAt: user.createdAt,
  });
}

export function useProcessMyUser() {
  const setUser = useAuth(s => s.setUser);
  return useCallback((user: User) => processMyUser(setUser, user), [setUser]);
}
